import React, { useState } from 'react';
import { Calendar } from "@/components/ui/calendar";
import { AlertCircle, Info, ShieldCheck } from 'lucide-react';
import { Button } from "@/components/ui/button";
import AmenitiesList from './AmenitiesList';
import ReservationDialog from './ReservationDialog';
import { useCalendar } from '@/contexts/CalendarContext';
import { usePricing } from '@/hooks/use-pricing';
import { trackEvent } from '@/lib/analytics';
import { Alert, AlertDescription } from "@/components/ui/custom-alert";
import { DateRange } from "react-day-picker";
import { fr } from "date-fns/locale";

const BookingSection = () => {
  const [dateRange, setDateRange] = React.useState<DateRange | undefined>({
    from: undefined,
    to: undefined
  });
  const [reservationDialogOpen, setReservationDialogOpen] = useState(false);
  
  const { isLoading, error, isDateDisabled } = useCalendar();
  const pricing = usePricing(dateRange?.from, dateRange?.to);

  // Calculer la prochaine date disponible
  const getNextAvailableDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let checkDate = new Date(today);

    // Chercher jusqu'à 365 jours dans le futur
    for (let i = 0; i < 365; i++) {
      if (!isDateDisabled(checkDate)) {
        return checkDate;
      }
      checkDate = new Date(checkDate);
      checkDate.setDate(checkDate.getDate() + 1);
    }
    return today;
  };

  // Mois à afficher : dates sélectionnées ou prochaine disponible
  const calendarMonth = dateRange?.from || getNextAvailableDate();
  
  const handleConfirmDates = () => {
    if (dateRange?.from && dateRange?.to) {
      trackEvent('dates_validated', {
        source: 'booking_section',
        from: dateRange.from.toISOString(),
        to: dateRange.to.toISOString(),
        nights: pricing?.nights,
      });
      setReservationDialogOpen(true);
    } else {
      alert('Veuillez sélectionner des dates d\'arrivée et de départ');
    }
  };
  
  return <>
      <div className="max-w-5xl mx-auto">
        <h2 className="text-3xl font-semibold mb-8 text-center">Disponibilités et Tarifs</h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold mb-4">À propos de ce logement</h3>
            <p className="text-gray-600 mb-6">
              Ce logement est une maison récente construite en 2022. Elle est située à moins de 400 mètres de la plage dans un quartier calme et résidentiel. Idéale pour passer d'agréables vacances en famille.
            </p>
            
            <AmenitiesList />
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold mb-4">Disponibilités</h3>
            
            {error && <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Impossible de charger les disponibilités: {error}
                </AlertDescription>
              </Alert>}
            
            {isLoading ? <div className="text-center py-8">
                <div className="animate-spin h-6 w-6 border-2 border-gray-900 border-t-transparent rounded-full mx-auto mb-2"></div>
                <p>Chargement des disponibilités...</p>
              </div> : <div className="space-y-3">
                <p className="text-sm">
                  <span className="inline-block h-3 w-3 bg-red-50 border border-red-300 rounded-sm mr-1"></span>
                  <span className="text-gray-600">Dates non disponibles</span>
                </p>
                <p className="text-sm">
                  <span className="inline-block h-3 w-3 bg-blue-100 border border-blue-300 rounded-sm mr-1"></span>
                  <span className="text-gray-600">Période sélectionnée</span>
                </p>
                
                <Calendar mode="range" selected={dateRange} onSelect={range => {
                  setDateRange(range);
                  if (range?.from && range?.to) {
                    trackEvent('dates_selected', {
                      source: 'booking_section',
                      from: range.from.toISOString(),
                      to: range.to.toISOString(),
                    });
                  }
                }} disabled={date => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return date < today || isDateDisabled(date);
            }} className="rounded-md border w-full pointer-events-auto" defaultMonth={calendarMonth} fromMonth={new Date()} numberOfMonths={1} locale={fr} weekStartsOn={1} />
                
                {dateRange?.from && dateRange?.to && <div className="mt-4 space-y-2">
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm font-medium text-blue-900">
                        Période sélectionnée : du {dateRange.from.toLocaleDateString('fr-FR')} au {dateRange.to.toLocaleDateString('fr-FR')}
                      </p>
                      <p className="text-xs text-blue-700 mt-1">
                        {pricing?.nights || 0} nuit(s)
                      </p>
                    </div>
                    
                    {pricing && <>
                        {pricing.error ? <Alert variant="warning" className="mt-2">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{pricing.error}</AlertDescription>
                          </Alert> : pricing.requiresQuote ? <Alert variant="default" className="mt-2 bg-amber-50 border-amber-200">
                            <Info className="h-4 w-4 text-amber-600" />
                            <AlertDescription className="text-amber-900">
                              Pour un séjour de plus de 7 nuits hors période estivale, nous vous contacterons avec un devis personnalisé.
                            </AlertDescription>
                          </Alert> : <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-700">Hébergement ({pricing.nights} nuit{pricing.nights > 1 ? 's' : ''})</span>
                                <span className="font-medium">{pricing.subtotal}€</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-700">Frais de ménage</span>
                                <span className="font-medium">{pricing.cleaningFee}€</span>
                              </div>
                              <div className="pt-2 border-t border-green-300 flex justify-between">
                                <span className="font-semibold text-gray-900">Total</span>
                                <span className="font-semibold text-lg text-green-700">{pricing.total}€</span>
                              </div>
                              {pricing.isSummer && <p className="text-xs text-gray-600 pt-1">
                                  Période estivale - Réservation par semaine
                                </p>}
                            </div>
                          </div>}
                      </>}
                  </div>}

                {/* Bouton de confirmation des dates */}
                <Button onClick={handleConfirmDates} disabled={isLoading || !dateRange?.from || !dateRange?.to} className="bg-[#86AA8E] hover:bg-opacity-90 h-10 w-full mt-4">
                  {isLoading ? "Chargement..." : "Vérifier disponibilité"}
                </Button>
                
                <p className="text-xs text-center text-gray-500 mt-3 flex items-center justify-center gap-1">
                  <ShieldCheck className="h-3 w-3" />
                  Aucun paiement en ligne. Votre demande de disponibilité sera envoyée par email.
                </p>
              </div>}
            
            <div className="mt-6">
              <h3 className="text-xl font-semibold mb-3">Tarifs</h3>
              <div className="space-y-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-900">Période estivale (4 juillet - 28 août)</p>
                  <p className="text-sm text-gray-600 mt-1">240€ / nuit</p>
                  <p className="text-xs text-gray-500 mt-1">Minimum 7 nuits</p>
                </div>
                
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-900">Hors période estivale</p>
                  <p className="text-sm text-gray-600 mt-1">De 480€ (2 nuits) à 940€ (7 nuits)</p>
                  <p className="text-xs text-gray-500 mt-1">Tarifs dégressifs selon la durée</p>
                  <p className="text-xs text-gray-500">Pour plus de 7 nuits : devis sur demande</p>
                </div>
              </div>
              
              <div className="mt-4 text-sm text-gray-600">
                
                
                
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dialogue de réservation */}
      <ReservationDialog open={reservationDialogOpen} onOpenChange={setReservationDialogOpen} arrivalDate={dateRange?.from} departureDate={dateRange?.to} pricing={pricing} />
    </>;
};
export default BookingSection;
